/**
 * Chat Actions
 * Redux actions for managing chat sessions and messages
 */

import { Dispatch } from "redux";
import {
	createChatSessionAPI,
	sendChatMessageAPI,
	loadChatSessionAPI,
	loadUserChatSessionsAPI,
	deleteChatSessionAPI,
	CreateChatSessionRequest,
	SendMessageRequest,
	ChatMessage,
	UserSession
} from "../../../services/chatService";
import {
	CHAT_CREATE_SESSION_REQUEST,
	CHAT_CREATE_SESSION_SUCCESS,
	CHAT_CREATE_SESSION_FAILURE,
	CHAT_SEND_MESSAGE_REQUEST,
	CHAT_SEND_MESSAGE_SUCCESS,
	CHAT_SEND_MESSAGE_FAILURE,
	CHAT_LOAD_SESSION_REQUEST,
	CHAT_LOAD_SESSION_SUCCESS,
	CHAT_LOAD_SESSION_FAILURE,
	CHAT_LOAD_USER_SESSIONS_REQUEST,
	CHAT_LOAD_USER_SESSIONS_SUCCESS,
	CHAT_LOAD_USER_SESSIONS_FAILURE,
	CHAT_SET_CURRENT_SESSION,
	CHAT_CLEAR_MESSAGES,
	CHAT_SET_AI_TYPING,
	CHAT_RESET_STATE,
	CHAT_ADD_OPTIMISTIC_MESSAGE,
	CHAT_UPDATE_MESSAGE_STATUS,
	CHAT_REMOVE_OPTIMISTIC_MESSAGE,
	CHAT_DELETE_SESSION_REQUEST,
	CHAT_DELETE_SESSION_SUCCESS,
	CHAT_DELETE_SESSION_FAILURE,
	CHAT_SET_SELECTED_MODEL
} from "../actionContants";

// ==================== Action Creators ====================

/**
 * Create a new chat session
 */
export const createChatSession = (request: CreateChatSessionRequest) => {
	return async (dispatch: Dispatch) => {
		dispatch({ type: CHAT_CREATE_SESSION_REQUEST });

		try {
			const response = await createChatSessionAPI(request);

			dispatch({
				type: CHAT_CREATE_SESSION_SUCCESS,
				payload: {
					sessionId: response.session_id,
					sessionTitle: response.session_title,
					message: response.message
				}
			});

			// Set as current session
			dispatch({
				type: CHAT_SET_CURRENT_SESSION,
				payload: response.session_id
			});

			return { success: true, data: response };
		} catch (error) {
			const errorMessage = error instanceof Error ? error.message : "Failed to create chat session";

			dispatch({
				type: CHAT_CREATE_SESSION_FAILURE,
				payload: errorMessage
			});

			return { success: false, error: errorMessage };
		}
	};
};

/**
 * Send a message to the current chat session
 */
export const sendChatMessage = (sessionId: string, request: SendMessageRequest) => {
	return async (dispatch: Dispatch) => {
		dispatch({ type: CHAT_SEND_MESSAGE_REQUEST });
		dispatch({ type: CHAT_SET_AI_TYPING, payload: true });

		try {
			const response = await sendChatMessageAPI(sessionId, request);

			dispatch({
				type: CHAT_SEND_MESSAGE_SUCCESS,
				payload: {
					userMessage: response.user_message,
					aiResponse: response.ai_response,
					message: response.message
				}
			});

			dispatch({ type: CHAT_SET_AI_TYPING, payload: false });

			return { success: true, data: response };
		} catch (error) {
			const errorMessage = error instanceof Error ? error.message : "Failed to send message";

			dispatch({
				type: CHAT_SEND_MESSAGE_FAILURE,
				payload: errorMessage
			});

			dispatch({ type: CHAT_SET_AI_TYPING, payload: false });

			return { success: false, error: errorMessage };
		}
	};
};

/**
 * Load chat session details and message history
 */
export const loadChatSession = (sessionId: string, limit?: number, offset?: number) => {
	return async (dispatch: Dispatch) => {
		dispatch({ type: CHAT_LOAD_SESSION_REQUEST });

		try {
			const response = await loadChatSessionAPI(sessionId, limit, offset);

			dispatch({
				type: CHAT_LOAD_SESSION_SUCCESS,
				payload: {
					sessionId: response.session_id,
					sessionTitle: response.session_title,
					messages: response.messages,
					totalMessages: response.total_messages,
					hasMore: response.has_more
				}
			});

			// Set as current session
			dispatch({
				type: CHAT_SET_CURRENT_SESSION,
				payload: response.session_id
			});

			return { success: true, data: response };
		} catch (error) {
			const errorMessage = error instanceof Error ? error.message : "Failed to load chat session";

			dispatch({
				type: CHAT_LOAD_SESSION_FAILURE,
				payload: errorMessage
			});

			return { success: false, error: errorMessage };
		}
	};
};

/**
 * Load user's chat sessions
 */
export const loadUserChatSessions = (userId: string, limit?: number, offset?: number) => {
	return async (dispatch: Dispatch) => {
		dispatch({ type: CHAT_LOAD_USER_SESSIONS_REQUEST });

		try {
			const response = await loadUserChatSessionsAPI(userId, limit, offset);

			dispatch({
				type: CHAT_LOAD_USER_SESSIONS_SUCCESS,
				payload: {
					sessions: response.sessions,
					totalSessions: response.total_sessions,
					hasMore: response.has_more
				}
			});

			return { success: true, data: response };
		} catch (error) {
			const errorMessage = error instanceof Error ? error.message : "Failed to load user sessions";

			dispatch({
				type: CHAT_LOAD_USER_SESSIONS_FAILURE,
				payload: errorMessage
			});

			return { success: false, error: errorMessage };
		}
	};
};

/**
 * Set the current active session
 */
export const setCurrentSession = (sessionId: string | null) => ({
	type: CHAT_SET_CURRENT_SESSION,
	payload: sessionId
});

/**
 * Clear messages (for new chat)
 */
export const clearChatMessages = () => ({
	type: CHAT_CLEAR_MESSAGES
});

/**
 * Set AI typing indicator
 */
export const setAITyping = (isTyping: boolean) => ({
	type: CHAT_SET_AI_TYPING,
	payload: isTyping
});

/**
 * Reset chat state
 */
export const resetChatState = () => ({
	type: CHAT_RESET_STATE
});

/**
 * Add optimistic message (shows immediately before API call)
 */
export const addOptimisticMessage = (message: ChatMessage) => ({
	type: CHAT_ADD_OPTIMISTIC_MESSAGE,
	payload: message
});

/**
 * Update message status (sending → sent → failed)
 */
export const updateMessageStatus = (messageId: string, status: "sending" | "sent" | "failed") => ({
	type: CHAT_UPDATE_MESSAGE_STATUS,
	payload: { messageId, status }
});

/**
 * Remove optimistic message (on failure)
 */
export const removeOptimisticMessage = (messageId: string) => ({
	type: CHAT_REMOVE_OPTIMISTIC_MESSAGE,
	payload: messageId
});

/**
 * Delete a chat session
 */
export const deleteChatSession = (sessionId: string) => {
	return async (dispatch: Dispatch) => {
		dispatch({ type: CHAT_DELETE_SESSION_REQUEST });

		try {
			await deleteChatSessionAPI(sessionId);

			dispatch({
				type: CHAT_DELETE_SESSION_SUCCESS,
				payload: sessionId
			});

			return { success: true };
		} catch (error) {
			const errorMessage = error instanceof Error ? error.message : "Failed to delete chat session";

			dispatch({
				type: CHAT_DELETE_SESSION_FAILURE,
				payload: errorMessage
			});

			return { success: false, error: errorMessage };
		}
	};
};

/**
 * Set the selected AI model
 */
export const setSelectedModel = (model: "claude" | "gemini" | "journey_ai") => ({
	type: CHAT_SET_SELECTED_MODEL,
	payload: model
});